import Vue from "vue";
import { mapState } from "vuex";
import { isNullOrUndefined, isDefined } from "../../../ceres/helper/utils";
import NotificationService from "../../../ceres/services/NotificationService";
import TranslationService from "../../../ceres/services/TranslationService";

export default Vue.component("shipping-profile-select", {
	props: {
		template: {
			type: String,
			default: "#vue-shipping-profile-select"
		},
		paddingClasses: {
			type: String,
			default: null
		},
		paddingInlineStyles: {
			type: String,
			default: null
		},
		paymentContainerIsOverwritten: {
			type: Boolean,
			default: false
		},
		deliveryTimeMap: {
			default: () => []
		},
		cutOffTime: {
			type: String,
			default: "14:00"
		},
		shippingWeekDays: {
			type: Array,
			// 0 for sunday, 6 for saturday
			default: () => [1, 2, 3, 4, 5]
		},
		deliveryWeekDays: {
			type: Array,
			default: () => [1, 2, 3, 4, 5, 6]
		},
		shippingHolidays: {
			type: Array,
			default: () => []
		},
		deliveryHolidays: {
			type: Array,
			default: () => []
		}
	},

	data() {
		return {
			shippingHolidayDates: [],
			deliveryHolidayDates: [],
			tomorrowDate: null,
			localeMap: {
				"de": "de-DE",
				"fr": "fr-FR",
				"pl": "pl-PL",
				"es": "es-ES",
				"it": "it-IT",
				"nl": "nl-NL",
				"cz": "cs-CZ",
				"pt": "pt-PT",
				"en": "en-GB"
			}
		};
	},

	computed: {
		labelStyles() {
			return (
				(this.paddingInlineStyles !== null ? this.paddingInlineStyles : "") +
				"" +
				(this.shippingProfileList.length === 1
					? "border-color:transparent;box-shadow:none;cursor:not-allowed;"
					: "")
			);
		},
		basketItemsHaveOnlyFirstAvail() {
			return (
				this.basketItems.filter(basketItem => basketItem.variation.data.variation.availability.id !== 1)
					.length === 0
			);
		},
		...mapState({
			shippingProfileList: state => state.checkout.shipping.shippingProfileList,
			maxDeliveryDays: state => state.checkout.shipping.maxDeliveryDays,
			shippingProfileId: state => state.checkout.shipping.shippingProfileId,
			showError: state => state.checkout.validation.shippingProfile.showError,
			isBasketLoading: state => state.basket.isBasketLoading,
			isCheckoutReadonly: state => state.checkout.readOnly,
			selectedPaymentMethodId: state => state.checkout.payment.methodOfPaymentId,
			basketItems: state => state.basket.items,
			billingAddressId: state => state.address.billingAddressId,
			deliveryAddressId: state => state.address.deliveryAddressId,
			shippingCountryId: state => state.basket.data.shippingCountryId
		})
	},

	/**
	 * Add a shipping provider
	 * Initialise the event listener
	 */
	created() {
		this.$store.commit("setShippingProfileValidator", this.validate);

		this.shippingHolidays.forEach(holidayList => {
			// each holidayList represents a year of holidays
			for (const holiday in holidayList) {
				const holidayDate = new Date(holidayList[holiday]);

				holidayDate.setHours(0, 0, 0, 0);
				this.shippingHolidayDates.push(holidayDate.getTime());
			}
		});

		this.deliveryHolidays.forEach(holidayList => {
			for (const holiday in holidayList) {
				const holidayDate = new Date(holidayList[holiday]);

				holidayDate.setHours(0, 0, 0, 0);
				this.deliveryHolidayDates.push(holidayDate.getTime());
			}
		});

		const todayDate = new Date();
		const tomorrowDate = new Date();

		tomorrowDate.setDate(todayDate.getDate() + 1);
		tomorrowDate.setHours(0, 0, 0, 0);

		this.tomorrowDate = tomorrowDate;
	},

	methods: {
		/**
		 * Method on shipping profile changed
		 */
		onShippingProfileChange(shippingProfileId) {
			this.$store
				.dispatch(
					"selectShippingProfile",
					this.shippingProfileList.find(
						shippingProfile => shippingProfile.parcelServicePresetId === shippingProfileId
					)
				)
				.then(
					data => {
						document.dispatchEvent(
							new CustomEvent("afterShippingProfileChanged", { detail: this.shippingProfileId })
						);
					},
					error => {
						// eslint-disable-next-line no-console
						console.log("error");
					}
				);

			this.validate();
		},

		validate() {
			const showError = this.shippingProfileId <= 0 || this.shippingProfileList.length <= 0;

			this.$store.commit("setShippingProfileShowError", showError);

			if (showError) {
				NotificationService.error(TranslationService.translate("Ceres::Template.checkoutCheckShippingProfile"));
			}
		},

		getTooltip(shippingProfileId) {
			let translationKey = "";

			const params = {};

			for (let i = 0; i < this.shippingProfileList.length; i++) {
				const shippingProfile = this.shippingProfileList[i];

				if (shippingProfile.parcelServicePresetId === shippingProfileId) {
					if (this.paymentContainerIsOverwritten) {
						translationKey = "Ceres::Template.checkoutChangePaymentMethodToHint";
						params.paymentMethodNames = shippingProfile.allowedPaymentMethodNames.join(",");
					} else {
						translationKey = "Ceres::Template.checkoutChangePaymentMethodHint";
					}
					break;
				}
			}

			return TranslationService.translate(translationKey, params);
		},

		isPaymentMethodExcluded(shippingProfile, selectedPaymentMethodId) {
			if (isDefined(shippingProfile.excludedPaymentMethodIds)) {
				return shippingProfile.excludedPaymentMethodIds.includes(selectedPaymentMethodId);
			} else {
				return false;
			}
		},

		getShippingProfileName(shippingProfile) {
			const shippingProfileId = shippingProfile.parcelServicePresetId;

			let shippingProfileName = shippingProfile.parcelServiceName;

			if (
				shippingProfileId in this.deliveryTimeMap &&
				"nameAlternative" in this.deliveryTimeMap[shippingProfileId] &&
				!isNullOrUndefined(this.deliveryTimeMap[shippingProfileId].nameAlternative)
			) {
				shippingProfileName = this.deliveryTimeMap[shippingProfileId].nameAlternative;
			}

			return shippingProfileName;
		},

		getDeliveryTimeText(shippingProfileId) {
			let deliveryTimeString = "";

			if (
				shippingProfileId in this.deliveryTimeMap &&
				(this.billingAddressId !== null || this.deliveryAddressId !== -99) &&
				this.selectedPaymentMethodId !== 6000 &&
				this.basketItemsHaveOnlyFirstAvail
			) {
				let hasCountryId = false;

				let from;

				let to;

				if (this.deliveryTimeMap[shippingProfileId].for.includes(this.shippingCountryId)) {
					({ from, to } = this.deliveryTimeMap[shippingProfileId]);
					hasCountryId = true;
				} else {
					const filteredAlternative = this.deliveryTimeMap[shippingProfileId].alternatives.filter(alternative => alternative.for.includes(this.shippingCountryId));

					if (filteredAlternative.length > 0) {
						({ from, to } = filteredAlternative[0]);
						hasCountryId = true;
					}
				}

				if (from && to) {
					const [ startDate, endDate ] = this.calculateDeliveryDateRange(
						from,
						to
					);

					deliveryTimeString = (
						this.isDateTomorrow(startDate) ?
						TranslationService.translate("Eros::Template.checkoutDeliveryTomorrow") :
						this.createLocalDateString(startDate)
					) + " - " + this.createLocalDateString(endDate);
				} else if (hasCountryId && "text" in this.deliveryTimeMap[shippingProfileId]) {
					deliveryTimeString = this.deliveryTimeMap[shippingProfileId].text;
				}
			}

			return deliveryTimeString;
		},

		getDeliveryTimeClass(shippingProfileId) {
			let classes = "";

			if (shippingProfileId in this.deliveryTimeMap && "class" in this.deliveryTimeMap[shippingProfileId]) {
				classes += this.deliveryTimeMap[shippingProfileId].class;
			}

			return classes;
		},

		calculateDeliveryDateRange(deliveryTimeMin, deliveryTimeMax) {
			const currentDate = new Date();
			const startDate = new Date(currentDate);
			const endDate = new Date(currentDate);
			const splitCutOffTime = this.cutOffTime.split(":");

			startDate.setHours(0, 0, 0, 0);
			endDate.setHours(0, 0, 0, 0);

			let safeGuard = 0;

			if (
				currentDate.getHours() >= splitCutOffTime[0] &&
				currentDate.getMinutes() >= splitCutOffTime[1]
			) {
				startDate.setDate(startDate.getDate() + 1);
				endDate.setDate(endDate.getDate() + 1);
			}

			// find the first shippable day
			while (((this.shippingWeekDays.length > 0 && !this.shippingWeekDays.includes(startDate.getDay())) ||
				(this.shippingHolidays.length > 0 && this.shippingHolidays.includes(startDate.getTime()))) &&
				++safeGuard < 999
			) {
				startDate.setDate(startDate.getDate() + 1);
				endDate.setDate(endDate.getDate() + 1);
			}

			// Add the delivery time (in days) to the start and end dates
			startDate.setDate(startDate.getDate() + deliveryTimeMin);
			endDate.setDate(endDate.getDate() + deliveryTimeMax);

			// if startDate is not shippable, find the next shippable day
			while (
				(
					(this.deliveryWeekDays.length > 0 && !this.deliveryWeekDays.includes(startDate.getDay())) ||
					(this.deliveryHolidays.length > 0 && this.deliveryHolidayDates.includes(startDate.getTime()))
				) &&
				++safeGuard < 999
			) {
				startDate.setDate(startDate.getDate() + 1);
				endDate.setDate(endDate.getDate() + 1);
			}

			// if endDate is not deliverable, find the next deliverable day
			while (((this.deliveryWeekDays.length > 0 && !this.deliveryWeekDays.includes(endDate.getDay())) ||
				(this.deliveryHolidays.length > 0 && this.deliveryHolidayDates.includes(endDate.getTime()))) &&
				++safeGuard < 999
			) {
				endDate.setDate(endDate.getDate() + 1);
			}

			if (safeGuard >= 999) {
				// eslint-disable-next-line no-console
				console.error("safeGuard triggered");
			}


			return [startDate, endDate];
		},

		isDateTomorrow: function (date) {
			return date.setHours(0, 0, 0, 0) === this.tomorrowDate.getTime();
		},

		createLocalDateString: function (date) {
			return date.toLocaleDateString(!(App.language in this.localeMap) ? this.localeMap.de : this.localeMap[App.language], {
				weekday: "short",
				day: "2-digit",
				month: "2-digit",
				formatMatcher: "best fit"
			}, {
				format: "dddd d. MMMM"
			}).replace(".,", ".");
		}
	}
});
